import React, { useContext, useEffect, useRef, useState } from 'react';
import { StyledSessionsListSection, MainMenu, MenuBtn, StyledMenuItem } from './SessionsListSection.style';
import sessionsService from '../../../services/sessionsServices';
import { TemporaryChatContext } from '../../../Context/temporaryChatContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Loaders from '../../Loaders';
import { MoreVertical } from 'lucide-react';
import OverlayModal from '../../ui/OverlayModal';
import RenameChatModal from '../RenameChatModal';
import DeleteChatModal from '../DeleteChatModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Toast from '../../Toast';
import { DEFAULT_AGENT_ID } from '../../../constants/defaultAgent';

function plainSessionTitle(title) {
  if (!title) return 'Untitled chat';
  return String(title)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim() || 'Untitled chat';
}

const SessionsListSection = ({ selectedAgent }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const { toggleRef, setSessionAgent, updateAgent, setSessionID } = useContext(TemporaryChatContext);

  const resolveAgentId = chat => chat?.agent_id || selectedAgent?.value || DEFAULT_AGENT_ID;

  const payload = {
    platform_id: 'knowhowlabs',
    agent_id: selectedAgent?.value,
    project_id: 'test',
  };

  const { data, isPending } = useQuery({
    queryKey: ['sessions', 'sidebar_sessions', payload],
    queryFn: async () => sessionsService.getSessionsList(payload),
  });

  const [chatDropdownOpen, setChatDropdownOpen] = useState(null);
  const [modal, setModal] = useState(null);

  const handleAddFavorite = useMutation({
    mutationFn: async chat => {
      return await sessionsService?.updateSession({
        platform_id: 'knowhowlabs',
        session_id: chat?.id,
        agent_id: resolveAgentId(chat),
        is_favorite: true,
      });
    },
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  const handleRemoveFavorite = useMutation({
    mutationFn: async chat => {
      return await sessionsService?.updateSession({
        platform_id: 'knowhowlabs',
        session_id: chat?.id,
        agent_id: resolveAgentId(chat),
        is_favorite: false,
      });
    },
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setChatDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const Timeline = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days'];
  const isActiveChat = id => location.pathname?.includes(id);

  const renderSessionItem = (chat, favorite = false) => (
    <li
      key={chat.id}
      ref={chatDropdownOpen === chat.id ? dropdownRef : null}
      className={isActiveChat(chat?.id) ? 'active' : undefined}>
      <span
        className="text"
        title={plainSessionTitle(chat?.title)}
        onClick={() => {
          toggleRef(true);
          if (chat?.agent_id) {
            const agentObj = {
              label: chat.agent_name || 'Agent',
              value: chat.agent_id,
              icon: chat.icon || null,
              mode: chat.mode || 'agent',
            };
            setSessionAgent(agentObj);
            updateAgent(agentObj);
          }
          setSessionID(chat?.id);
          navigate(`/chat/${chat?.id}`);
        }}>
        {plainSessionTitle(chat?.title)}
      </span>
      <MainMenu
        portal
        align="end"
        direction="bottom"
        menuButton={
          <MenuBtn type="button" aria-label="Chat actions">
            <MoreVertical size={16} strokeWidth={2.25} />
          </MenuBtn>
        }>
        <StyledMenuItem>
          <button
            type="button"
            onClick={() => (favorite ? handleRemoveFavorite.mutate(chat) : handleAddFavorite.mutate(chat))}>
            {favorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </button>
        </StyledMenuItem>
        <StyledMenuItem onClick={() => setModal({ type: 'rename', chat })}>
          <button type="button">Rename</button>
        </StyledMenuItem>
        <StyledMenuItem onClick={() => setModal({ type: 'delete', chat })}>
          <button type="button" style={{ color: 'rgba(215, 65, 32, 1)' }}>
            Delete
          </button>
        </StyledMenuItem>
      </MainMenu>
    </li>
  );

  return (
    <StyledSessionsListSection>
      {isPending ? (
        <Loaders textLoader />
      ) : (
        <>
          {data?.sessions?.['Favorite']?.length > 0 && (
            <>
              <span className="subheading2">Favorites</span>
              <ul className="chat-list">
                {data.sessions.Favorite.map(chat => renderSessionItem(chat, true))}
              </ul>
            </>
          )}

          {Timeline.map(time =>
            data?.sessions?.[time]?.length > 0 ? (
              <div key={time}>
                <span className="subheading2">{time}</span>
                <ul className="chat-list">{data.sessions[time].map(chat => renderSessionItem(chat))}</ul>
              </div>
            ) : null,
          )}
        </>
      )}

      {modal?.type === 'rename' && (
        <OverlayModal title="Edit Chat Title" onClose={() => setModal(null)} width={420} placement="center">
          <RenameChatModal onClose={() => setModal(null)} data={modal.chat} />
        </OverlayModal>
      )}
      {modal?.type === 'delete' && <DeleteChatModal onClose={() => setModal(null)} data={modal.chat} />}
    </StyledSessionsListSection>
  );
};

export default SessionsListSection;
